/**
 * Pulsez v2 — shared contracts between the Worker, the Durable Objects, the Workflow, the web client
 * and the end-to-end tests. Every module MUST use these names and shapes; change them here first.
 *
 * Conventions
 * - Ids are ULIDs (see ./ids.ts). Timestamps are epoch milliseconds (number).
 * - All RPC arguments and results are JSON-serialisable (no class instances, no Dates).
 * - User-facing strings are French.
 */
import type { WatchesView, WatchOp } from "./watches";

// ───────────────────────────── Settings ─────────────────────────────

export type Persona = "chaleureux" | "pro" | "direct" | "drole" | "zen" | "coach";
export const PERSONA_VALUES = ["chaleureux", "pro", "direct", "drole", "zen", "coach"] as const satisfies readonly Persona[];
/** How many emojis the agent puts in its messages ("auto": its own judgement, as before the setting existed). */
export type EmojiLevel = "auto" | "none" | "few" | "many";
export const EMOJI_LEVELS = ["auto", "none", "few", "many"] as const satisfies readonly EmojiLevel[];
/** Preferred length of its messages ("auto": what the channel and the question call for). Channel limits always win. */
export type MessageLength = "auto" | "short" | "detailed";
export const MESSAGE_LENGTHS = ["auto", "short", "detailed"] as const satisfies readonly MessageLength[];
/** How it addresses the user in languages that make the difference (« tu » / « vous », du / Sie…). */
export type Formality = "tu" | "vous";
export const FORMALITIES = ["tu", "vous"] as const satisfies readonly Formality[];
/** Grammatical gender it uses for itself (French agreements: « prêt » / « prête »). */
export type AgentGender = "masculin" | "feminin" | "neutre";
export const AGENT_GENDERS = ["masculin", "feminin", "neutre"] as const satisfies readonly AgentGender[];
/** The agent's color: the app's accent color while it is the current agent (tuned light and dark values, client side). */
export type AccentColor = "violet" | "bleu" | "menthe" | "ambre" | "rose" | "graphite";
export const ACCENT_COLORS = ["violet", "bleu", "menthe", "ambre", "rose", "graphite"] as const satisfies readonly AccentColor[];
/**
 * The drawn character (« Perso », docs/design/APP-APPLE.md §4; shared/character.ts) chosen in « Son visage »: its body
 * (rond, fleur, goutte, pilule) and one of the seven « Nuit » colors. Null in AgentSettings = the default face (the main
 * agent's white « rond », a look derived from the name for the others).
 * The first design's values stay valid (shapes galet, haricot, nuage; colors terre, miel, sauge, ardoise, lavande,
 * cacao): they are drawn as the nearest V2 look (galet → rond, haricot → pilule, nuage → fleur; colors: the nearest
 * Nuit color). `finish`, `eyes` and `accessory` belong to that first design: optional, kept when stored, not drawn.
 */
export const CHARACTER_SHAPES_V2 = ["rond", "fleur", "goutte", "pilule"] as const;
export const CHARACTER_COLORS_V2 = ["blanc", "mandarine", "cerise", "rose", "bleuet", "menthe", "tournesol"] as const;
export const CHARACTER_SHAPES = [...CHARACTER_SHAPES_V2, "galet", "haricot", "nuage"] as const;
export const CHARACTER_COLORS = [...CHARACTER_COLORS_V2, "terre", "miel", "sauge", "ardoise", "lavande", "cacao"] as const;
export const CHARACTER_FINISHES = ["mat", "gloss", "velours"] as const;
export const CHARACTER_EYES = ["ronds", "grands", "joyeux", "endormis"] as const;
export const CHARACTER_ACCESSORIES = ["aucun", "pousse", "casque", "bonnet", "lunettes"] as const;
export interface AgentCharacter {
  shape: (typeof CHARACTER_SHAPES)[number];
  color: (typeof CHARACTER_COLORS)[number];
  /** First design only (not drawn). */
  finish?: (typeof CHARACTER_FINISHES)[number];
  eyes?: (typeof CHARACTER_EYES)[number];
  accessory?: (typeof CHARACTER_ACCESSORIES)[number];
}
/** Style presets of a generated avatar (companion characters, never a logo). */
export type AvatarStyle = "3d" | "anime" | "flat" | "pixel";
export const AVATAR_STYLES = ["3d", "anime", "flat", "pixel"] as const satisfies readonly AvatarStyle[];

/**
 * Agent identity. Today every account has ONE agent, "main" (its PulseAgent, named after the user id). Everything
 * that makes an agent someone (name, emoji, color, personality, way of writing, avatar) is stored in that agent's own
 * settings, and its files under u/<userId>/agents/<agentId>/, so more agents per user add objects, not a rewrite.
 */
export const MAIN_AGENT_ID = "main";

/** R2 prefix (FILES) of an agent's avatars: inside the user's own prefix, so ownership checks and deletion cover it. */
export function avatarKeyPrefix(userId: string, agentId: string = MAIN_AGENT_ID): string {
  return `u/${userId}/agents/${agentId}/avatar/`;
}

/** The agent's face: an image of the user's own files (R2 FILES, served by GET /api/files/* to its owner only). */
export interface AgentAvatar {
  /** u/<userId>/agents/<agentId>/avatar/<ulid>.<png|jpg|webp> */
  fileKey: string;
  source: "generated" | "upload";
  /** Generated avatars: the style preset and the user's own description. */
  style?: AvatarStyle;
  prompt?: string;
  updatedAt: number;
}

export type Autonomy = "prudent" | "equilibre" | "autonome";
export type Effort = "low" | "medium" | "high";
/** "sms" = a Sendblue message whose service is SMS/RCS (sender not verified by Apple): treated like email, as untrusted. */
export type Channel = "web" | "imessage" | "sms" | "telegram" | "email";
export type MachineTarget = "none" | "cloud" | `local:${string}`;

export interface AgentSettings {
  agentName: string; // "Nova"
  agentEmoji: string; // "✨"
  persona: Persona;
  personaNotes: string; // free text from the user ("tutoie-moi, sois bref")
  userName: string;
  timezone: string; // IANA, e.g. "Europe/Paris"
  /** ISO 639-1 code of the language the agent writes in ("fr" by default); fixed texts are French or English. */
  language: string;
  briefEnabled: boolean;
  briefTime: string; // "HH:MM" local
  recapEnabled: boolean;
  recapTime: string; // "HH:MM" local
  checkinMinutes: number; // 15..240
  activeStart: string; // "HH:MM" local — proactive work only inside [activeStart, activeEnd)
  activeEnd: string;
  quietStart: string; // "HH:MM" local — proactive messages held inside [quietStart, quietEnd) (may wrap midnight)
  quietEnd: string;
  weekends: boolean; // proactive check-ins on Saturday/Sunday
  /** null (default) = no daily cap; 0..20 = at most N proactive pushes per local day, set by the user (beyond: in-app only). */
  dailyProactiveCap: number | null;
  urgentBypassQuiet: boolean;
  autonomy: Autonomy;
  primaryChannel: Channel; // where proactive messages go (falls back to web + push when not linked)
  effortChat: Effort;
  defaultMachine: MachineTarget;
  /** Hours between read-only "veille" sweeps of connected apps during active hours (0 = off). */
  sweepHours: number;
  /** Brief and recap also on Saturday/Sunday (check-ins/sweeps follow `weekends`). */
  briefWeekends: boolean;
  /** Proactive messages paused until this epoch ms (user reminders still delivered). */
  snoozeUntil: number | null;
  /** Topics the user does not want to be pinged about (matched by the triage). */
  mutedTopics: string[];
  onboarded: boolean;
  /**
   * Model that drives a screen (clicks, typing, scrolling) in a mission: "auto" (GPT computer use in fast mode when
   * the instance has an OpenAI key, else Claude), "gpt" or "claude".
   */
  computerModel: "auto" | "gpt" | "claude";
  /** Way of writing (« Ton agent »): the default values leave the agent as it was before these settings existed. */
  emojiLevel: EmojiLevel;
  messageLength: MessageLength;
  formality: Formality;
  agentGender: AgentGender;
  accentColor: AccentColor;
  /** Set only by the avatar routes (generated or uploaded); null = the placeholder drawn from the name. */
  avatar: AgentAvatar | null;
  /** The drawn character's look (shown when there is no avatar image); null = derived from the name. */
  character: AgentCharacter | null;
  /**
   * Read-only, never stored nor accepted in a patch. Formerly the daily cap lowered from the user's feedback; the
   * relevance learner replaced it, so it is always null now (kept for older clients).
   */
  learnedProactiveCap?: number | null;
}

export const DEFAULT_SETTINGS: AgentSettings = {
  agentName: "Nova",
  agentEmoji: "✨",
  persona: "chaleureux",
  personaNotes: "",
  userName: "",
  timezone: "Europe/Paris",
  language: "fr",
  briefEnabled: true,
  briefTime: "07:30",
  recapEnabled: false,
  recapTime: "19:00",
  checkinMinutes: 60,
  activeStart: "08:00",
  activeEnd: "21:30",
  quietStart: "22:00",
  quietEnd: "07:30",
  weekends: true,
  dailyProactiveCap: null,
  urgentBypassQuiet: false,
  autonomy: "equilibre",
  primaryChannel: "web",
  effortChat: "medium",
  defaultMachine: "cloud",
  sweepHours: 3,
  briefWeekends: true,
  snoozeUntil: null,
  mutedTopics: [],
  onboarded: false,
  computerModel: "auto",
  emojiLevel: "auto",
  messageLength: "auto",
  formality: "tu",
  agentGender: "masculin",
  accentColor: "violet",
  avatar: null,
  character: null,
};

// ───────────────────────────── Chat ─────────────────────────────

export type ChatKind = "chat" | "proactive" | "brief" | "approval" | "mission" | "system";

export interface ChatMessage {
  id: string;
  direction: "in" | "out";
  channel: Channel;
  kind: ChatKind;
  text: string;
  createdAt: number;
  turnId?: string;
  /** Per-channel delivery state of an outgoing message. */
  delivery?: Partial<Record<Channel | "push", "pending" | "held" | "sent" | "failed" | "in_app">>;
  /** Related object (approval id, brief id, run id) for deep links in the UI. */
  ref?: { type: "approval" | "brief" | "run" | "mission"; id: string };
  /** A tapback (iMessage/SMS reaction) on an earlier message: shown compactly, never answered. */
  reaction?: ChatReaction;
  /** Files sent with this line (send_file): images inline, other files as download links. */
  files?: ChatFile[];
  /** The line this one answers as an inline reply (iMessage, Telegram, or the web chat's tap-to-answer). */
  replyTo?: { id: string; text: string; direction: "in" | "out" };
  /**
   * The line asks the owner something answerable in the conversation (a mission question or an approval): the web chat
   * shows tap-to-answer buttons while it is pending.
   */
  ask?: ChatAsk;
  /**
   * A generated interface shown under the text (show_ui: options to pick, a timeline, figures, a link…), on lines of
   * the web/app channel only: elsewhere its plain-text version is already in `text`.
   */
  ui?: ChatUiSpec;
}

/**
 * The generated interface of a chat line (docs/design/APP-APPLE.md §6.7; catalog and validation:
 * src/shared/chat-ui.ts): the flat json-render tree of Pulse's summary. The server only ever sends a validated spec
 * (catalog types and props, at most 24 elements and 4 levels, children of the allowed kinds), so every element renders.
 */
export interface ChatUiSpec {
  root: string;
  elements: Record<string, ChatUiElement>;
}

export const CHAT_UI_COMPONENTS = [
  "Stack",
  "Row",
  "Headline",
  "Text",
  "DayTimeline",
  "TimelineItem",
  "Highlight",
  "Stat",
  "PersonChip",
  "Weather",
  "Progress",
  "Options",
  "Option",
  "LinkCard",
  "Sparkline",
  "Checklist",
  "CheckItem",
  "ActionChip",
  "Table",
  "Products",
  "Product",
  "Journey",
] as const;
export type ChatUiComponent = (typeof CHAT_UI_COMPONENTS)[number];

export interface ChatUiElement {
  type: ChatUiComponent;
  /** The component's props; optional props that are not set are left out. */
  props: Record<string, unknown>;
  /** Keys of its children, in order (empty for a leaf). */
  children: string[];
  /** Set on the pressable components (Option, ActionChip, LinkCard, Product, Journey), and only on them. */
  on?: { press: ChatUiAction };
  /**
   * A Product's photo as stored by show_ui (R2 key u/<userId>/outbound/<ULID>.<ext>, served by GET /api/files/*): set
   * by the server only, never taken from the model's spec.
   */
  photo?: string;
}

/** Pages an `open` action may lead to (never an outside address). */
export const CHAT_UI_ROUTES = ["/", "/discussion", "/approbations", "/missions", "/veilles", "/memoire", "/connexions", "/ordinateur"] as const;
export type ChatUiRoute = (typeof CHAT_UI_ROUTES)[number];

/**
 * What a press does. ask: sends `message` as the owner's message (POST /api/chat, replyTo = the line carrying the
 * interface). open: goes to a page of the app. url: opens an https address in the browser, never inside the app.
 */
export type ChatUiAction =
  | { action: "ask"; params: { message: string } }
  | { action: "open"; params: { to: ChatUiRoute } }
  | { action: "url"; params: { href: string } };

/** A question or approval asked in the conversation (agent/pending.ts). */
export interface ChatAsk {
  type: "question" | "approval";
  id: string;
  status: "pending" | "done";
  /** Choices offered by a mission question (empty: a yes/no answer or free text). */
  choices?: string[];
  /** An approval that only a typed « oui » decides (payments, critical actions): no one-tap approve button. */
  wordOnly?: boolean;
}

/** A file attached to a chat line (outbound: send_file). Rendered by the web chat through GET /api/files/<key>. */
export interface ChatFile {
  /** Outbound file id (ULID). */
  id: string;
  /** R2 key under u/<userId>/ (never built from model or provider text by the client). */
  key: string;
  /** Display name with its real extension. */
  name: string;
  /** Canonical type, decided from the bytes. */
  mime: string;
  /** Bytes. */
  size: number;
  /** "image" = png/jpeg/gif/webp only (shown inline); everything else is a download. */
  kind: "image" | "file";
  width?: number;
  height?: number;
  /** Deleted by the 30-day housekeeping. */
  expired?: true;
  /**
   * The image of the line's generated interface (show_ui on iMessage, SMS, Telegram): the line keeps the interface's spec
   * for the agent's own reading, the app shows only this image (ChatMessage.ui is then left out).
   */
  uiImage?: true;
}

/** The six tapbacks the web and native apps send (POST /api/chat/:id/react), in the picker's order. */
export const WEB_REACTIONS = ["❤️", "👍", "👎", "😂", "‼️", "❓"] as const;

export interface ChatReaction {
  /** ❤️ 👍 👎 😂 ‼️ ❓ or the emoji of an emoji tapback. */
  emoji: string;
  /** The tapback was taken back (« Removed a heart from … »). */
  removed?: boolean;
  /** The chat line it quotes, when the quoted text matched a recent message. */
  targetId?: string;
  /** Sent by the agent (a tapback on one of the owner's messages) rather than by the owner. */
  byAgent?: boolean;
}

// ───────────────────────────── Approvals ─────────────────────────────

export type Risk = "read" | "write" | "external" | "critical" | "forbidden";
export type ApprovalStatus = "pending" | "approved" | "rejected" | "expired" | "executed" | "failed";

export interface ApprovalView {
  id: string;
  /** Four characters from an unambiguous alphabet; the user replies "OUI K7M3" / "NON K7M3" (iMessage/Telegram only). */
  code: string;
  /** Whether a text reply with the code may decide it (false: critical, or too long to show completely by text). */
  textDecision?: boolean;
  /** Rendered server-side from the exact stored action (never from the model's words). */
  title: string;
  lines: { label: string; value: string }[];
  risk: Exclude<Risk, "read" | "forbidden">;
  kind: "app_action" | "agent_action" | "mission" | "computer_session" | "computer_action" | "message";
  status: ApprovalStatus;
  createdAt: number;
  expiresAt: number;
  decidedAt?: number;
  decidedVia?: "web" | "telegram" | "sms" | "imessage" | "expiry";
  source: "chat" | "mission";
  runId?: string;
  /** The model's one-line justification, shown as "Raison invoquée par l'agent" (untrusted). */
  reason?: string;
  /** For app actions: the exact tool slug (immutable) and editable arguments (web UI only). */
  action?: { toolSlug: string; args: Record<string, unknown> };
  /** The kind of gated action (rules vocabulary: send_email, payment, local_computer…), when it has one. */
  ruleAction?: import("./rules").GatedAction;
  /**
   * What « Toujours autoriser » / « TOUJOURS <code> » would stop asking for (French words), when the action can be
   * turned into an allow rule; absent otherwise (payments, deletions, actions without a recipient to scope them).
   */
  always?: string;
  result?: string;
  error?: string;
}

export interface DecideApprovalInput {
  id: string;
  decision: "approve" | "reject";
  via: "web" | "telegram" | "sms" | "imessage";
  /** Web UI only: replaces the stored arguments (same tool slug). */
  editedArgs?: Record<string, unknown>;
  note?: string;
  /** With decision "approve": also create the allow rule(s) the approval offers (« Toujours autoriser »). */
  always?: boolean;
  /** A decision by message: by its code, a typed word bound to it, a tapback on its line, or a Telegram button. */
  how?: "code" | "word" | "tapback" | "button";
}

// ───────────────────────────── Briefs ─────────────────────────────

export interface BriefCard {
  title: string;
  body: string;
  category: string; // "agenda" | "emails" | "missions" | "rappel" | "actu" | "meteo" | …
  emoji?: string;
  priority?: "high" | "normal";
  url?: string; // https only (validated)
}

export interface Brief {
  id: string;
  kind: "morning" | "evening";
  localDate: string; // YYYY-MM-DD in the user's timezone
  headline: string;
  intro: string;
  cards: BriefCard[];
  createdAt: number;
}

// ───────────────────────────── Missions & runs ─────────────────────────────

export type MissionStatus = "active" | "paused" | "done" | "cancelled";
export type RunStatus = "queued" | "running" | "waiting_approval" | "waiting_user" | "done" | "failed" | "cancelled";

export interface MissionView {
  id: string;
  title: string;
  goal: string;
  cadenceMinutes: number | null; // null = one-off / on demand
  machine: MachineTarget;
  status: MissionStatus;
  notes: string;
  nextRunAt: number | null;
  lastRunId: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface MissionRunView {
  id: string;
  missionId: string | null;
  title: string;
  instructions: string;
  machine: MachineTarget;
  status: RunStatus;
  iter: number;
  result: string | null;
  error: string | null;
  costUsd: number;
  createdAt: number;
  startedAt: number | null;
  finishedAt: number | null;
  /** Pending question for the user when status = waiting_user. */
  question?: string | null;
  /** Id and text code ("Q4") of that pending question (answers by SMS/iMessage start with the code). */
  questionId?: string | null;
  questionCode?: string | null;
  /** Pending approval id when status = waiting_approval. */
  approvalId?: string | null;
  /** Who carries the run out: the Claude loop, or GPT computer use driving the screen ("openai") — absent until decided. */
  executor?: "claude" | "openai";
  /** Parallel lanes: the fan-out (computer_lanes) this run belongs to, and its index in it (0-based). */
  groupId?: string;
  lane?: number;
}

/** One entry of a run timeline, derived from the mission transcript. */
export type RunTimelineEntry =
  | { kind: "text"; at: number; text: string }
  | { kind: "thinking"; at: number; text: string }
  | { kind: "action"; at: number; tool: string; summary: string; ok: boolean }
  | { kind: "screenshot"; at: number; fileKey: string }
  | { kind: "question"; at: number; text: string }
  | { kind: "answer"; at: number; text: string };

export interface RunDetail {
  run: MissionRunView;
  timeline: RunTimelineEntry[];
}

/** Returned by PulseAgent.missionIterate to the MissionWorkflow driver. */
export interface MissionIterResult {
  status: "continue" | "done" | "failed" | "cancelled" | "waiting_approval" | "waiting_user";
  /** Workflow event type to wait for: `approval-<approvalId>` or `answer-<questionId>`. */
  waitFor?: string;
  /** Upper bound for the wait, e.g. "72 hours". */
  waitTimeout?: string;
  /** With status "continue": seconds the workflow sleeps before the next iteration (transient model errors). */
  backoffS?: number;
  /**
   * With status "continue": the iteration only waited (the owner holds the controls of the cloud computer, or the
   * computer is still booting; no model call): the workflow does not count it against missionMaxIters.
   */
  paused?: boolean;
}

// ───────────────────────────── Signals ─────────────────────────────

export interface SignalInput {
  /** Provider event id (dedupe key), e.g. "composio:<webhook-id>". */
  id: string;
  source: string; // "gmail" | "googlecalendar" | "slack" | "email" | "machine" | …
  kind: string; // "new_email" | "event_created" | "dm" | …
  title: string;
  summary?: string;
  payload: unknown; // untrusted
  occurredAt?: number;
}

export interface SignalView {
  id: string;
  source: string;
  kind: string;
  title: string;
  summary: string | null;
  receivedAt: number;
  status: "new" | "claimed" | "handled" | "ignored";
}

// ───────────────────────────── Activity ─────────────────────────────

export type TurnTrigger =
  | "user_message"
  | "onboarding"
  | "morning_brief"
  | "evening_recap"
  | "checkin"
  | "wakeup"
  | "follow_up"
  | "mission_result"
  | "approval_decision"
  | "question_answer"
  | "device_event"
  | "sweep"
  | "meeting_prep"
  | "digest"
  | "re_engagement"
  | "agent_message"
  | "compaction";

export interface TurnView {
  id: string;
  conv: string;
  trigger: TurnTrigger;
  status: "queued" | "running" | "done" | "failed" | "cancelled";
  createdAt: number;
  startedAt: number | null;
  finishedAt: number | null;
  costUsd: number;
  error: string | null;
  tools: { name: string; ok: boolean }[];
}

export interface UsageView {
  monthUsd: number;
  todayUsd: number;
  userBudgetUsd: number;
  globalMonthUsd?: number; // owner only
  globalBudgetUsd?: number; // owner only
}

export interface Dashboard {
  settings: AgentSettings;
  latestBrief: Brief | null;
  approvals: ApprovalView[]; // pending first
  missions: MissionView[];
  runs: MissionRunView[]; // recent
  recentProactive: ChatMessage[];
  machines: MachineView[];
  usage: UsageView;
  status: AgentStatus;
}

export interface AgentStatus {
  state: "idle" | "working";
  activeTurnId: string | null;
  nextBriefAt: number | null;
  nextCheckinAt: number | null;
}

// ───────────────────────────── Memory ─────────────────────────────

export type MemoryKind =
  | "fact"
  | "preference"
  | "relationship"
  | "event"
  | "instruction"
  | "plan"
  | "task"
  | "agent_action"
  | "agent_opinion"
  | "insight";

export interface MemoryItemView {
  id: string;
  kind: MemoryKind;
  content: string;
  topicKey: string | null;
  status: "active" | "superseded" | "invalidated" | "archived";
  validFrom: number | null;
  validTo: number | null;
  recordedAt: number;
  dueAt: number | null;
  importance: number; // 1..5
  pinned: boolean;
  source: "extraction" | "user" | "agent_tool" | "consolidation" | "import";
  entities: string[];
  /** Lowest trust among the fact's sources. Only "user" facts may shape the agent's instructions. */
  trust?: "user" | "agent" | "external" | "import";
  dueDateOnly?: boolean;
  sensitivity?: MemorySensitivity;
  /** Accounts with several agents: the name of the agent that learned it (they share one memory). */
  learnedBy?: string;
}

/** "Pourquoi tu sais ça ?" — where a memory comes from and what it replaced. */
export interface MemoryExplanation {
  item: MemoryItemView;
  /** Evidence quotes, newest first (≤ 10). channel = episode source ("chat", "imessage", "email", "composio:gmail"…). */
  sources: { quote: string; channel: string; trust: "user" | "agent" | "external"; at: number | null }[];
  /** Older versions this memory replaced, newest first (≤ 10). */
  history: MemoryItemView[];
  replacedBy: MemoryItemView | null;
}

/** E2E-only memory operations (MemoryStoreRPC.e2e throws unless config(env).e2e). */
export type MemoryE2eOp =
  | { op: "inspect" }
  | { op: "export" }
  | { op: "readExport"; key: string; lines?: number }
  | { op: "consolidateNow"; phases?: string[] }
  | { op: "setExtractTiming"; debounceS: number; pauseS: number; maxWaitS: number }
  | { op: "setClock"; offsetMs: number }
  | { op: "wipe" }
  | { op: "recall"; query: string; audience?: MemoryAudience }
  | {
      op: "seed";
      facts: {
        content: string;
        kind: MemoryKind;
        trust: "user" | "agent" | "import" | "external";
        source: MemoryItemView["source"];
        pinned?: boolean;
        importance?: number;
        sensitivity?: MemorySensitivity;
        quote?: string;
        ageDays?: number;
        topicKey?: string;
        validFrom?: number;
        dueAt?: number;
      }[];
    }
  | { op: "facts"; ids?: string[] }
  | { op: "armNightly"; inMs?: number };

/** Why the nightly sleep set a memory aside (archived, restorable). */
export type MemoryDropReason = "trivial" | "transient" | "redundant" | "outdated" | "meta";

/** The memory page's sleep view (GET /api/memory/sleep). */
export interface MemorySleepReport {
  /** Last nightly run: memories merged (absorbed into another), set aside, reread. null before the first night. */
  last: { at: number; merged: number; dropped: number; slept: number } | null;
  /** Memories set aside by the sleep, newest first. */
  dropped: (MemoryItemView & { dropReason: MemoryDropReason; droppedAt: number })[];
}

export interface ProfileBlockView {
  label: "identite" | "preferences" | "proches" | "projets" | "routines" | "consignes";
  content: string;
  updatedAt: number;
}

/**
 * Suffix of the source of an episode that holds a voice note's transcript (« imessage:vocal »): the user's words, maybe
 * someone else's forwarded recording — the memory never takes a standing instruction from it.
 */
export const VOICE_EPISODE_SUFFIX = ":vocal";

export function isVoiceEpisodeSource(source: string): boolean {
  return source.endsWith(VOICE_EPISODE_SUFFIX);
}

export interface EpisodeInput {
  source: string; // "chat" | "imessage" | "telegram" | "email" | "composio:gmail" | "tool" | "agent" | "mission" | "<channel>:vocal"
  role: "user" | "assistant" | "tool" | "external";
  trust: "user" | "agent" | "external";
  content: string;
  occurredAt: number;
  sessionId?: string; // conv / turn id
  /** Accounts with several agents: the name of the agent that took part (its extracted memories say so). */
  agent?: string;
}

export type MemorySensitivity = "normal" | "private" | "care";
/** Who the recall is for: proactive turns and missions never see "private"/"care" memories unless directly relevant. */
export type MemoryAudience = "main" | "proactive" | "mission";
export type MemoryForgetReason = "incorrect" | "too_private" | "outdated";

export interface RecallQuery {
  query: string;
  audience?: MemoryAudience; // default "main"
  hypotheticalAnswer?: string; // HyDE
  since?: number;
  until?: number;
  includeHistory?: boolean; // include superseded/invalidated facts
  limit?: number; // default 12
  budgetTokens?: number; // default 1200 (auto-recall) / 4000 (tool)
  kinds?: MemoryKind[];
}

export interface RecallResult {
  items: (MemoryItemView & { score: number; reasons?: string[] })[];
  /** Ready-to-inject French block with dates and ids; "" when nothing is relevant (abstention). */
  text: string;
}

export interface SaveMemoryInput {
  content: string;
  kind: MemoryKind;
  importance?: number;
  topicKey?: string;
  dueAt?: number;
  /** The due date has no time of day (it goes into that day's brief; it never pings at a given hour). */
  dueDateOnly?: boolean;
  pinned?: boolean;
  source: "user" | "agent_tool" | "import";
  /** Who the content comes from: the user's own words, the agent, or outside content (emails, web, apps). */
  trust?: "user" | "agent" | "external";
  sensitivity?: MemorySensitivity;
  /** Correction: this memory replaces that one (history kept, same topic). */
  supersedesId?: string;
  /** Accounts with several agents: the name of the agent saving it. */
  learnedBy?: string;
}

export interface MemoryListQuery {
  kind?: MemoryKind;
  status?: MemoryItemView["status"];
  q?: string;
  cursor?: string;
  limit?: number;
}

// ───────────────────────────── Machines ─────────────────────────────

export type MachineSizeName = "standard-1" | "standard-2" | "standard-3" | "standard-4";
export type MachineSize = MachineSizeName | { vcpu: number; memoryMib: number; diskMb: number };

export const MACHINE_SIZES: Record<MachineSizeName, { vcpu: number; memoryGiB: number; diskGB: number; usdPer30DaysAlwaysOn: number; label: string }> = {
  "standard-1": { vcpu: 0.5, memoryGiB: 4, diskGB: 8, usdPer30DaysAlwaysOn: 27, label: "Essentiel" },
  "standard-2": { vcpu: 1, memoryGiB: 6, diskGB: 12, usdPer30DaysAlwaysOn: 41, label: "Confort" },
  "standard-3": { vcpu: 2, memoryGiB: 8, diskGB: 16, usdPer30DaysAlwaysOn: 55, label: "Puissant" },
  "standard-4": { vcpu: 4, memoryGiB: 12, diskGB: 20, usdPer30DaysAlwaysOn: 81, label: "Maximum" },
};

export interface MachinePolicy {
  /** Local machines: folders the agent may read/write (absolute paths on the device). */
  allowedRoots: string[];
  /** Local machines: GUI control allowed while nobody is at the machine. */
  unattendedGui: boolean;
  /** Shell allowed at all (local machines). */
  shell: boolean;
}

export interface MachineView {
  id: string;
  kind: "cloud" | "local";
  name: string;
  /**
   * cloud: stopped|starting|running|sleeping|stopping|unavailable — local: online|offline. « sleeping »: light sleep, the
   * container is up with its desktop programs frozen; any action wakes it in a second or two.
   */
  state: "stopped" | "starting" | "running" | "sleeping" | "stopping" | "unavailable" | "online" | "offline";
  size?: MachineSize;
  idleStopMinutes?: number; // cloud: minutes without activity before the light sleep; 0 = always on
  /** cloud: the full stop after the light sleep — "auto" (the owner's rhythm) or minutes asleep (0 = no sleep). */
  sleepStop?: "auto" | number;
  /** cloud, while it sleeps: since when, what an hour of sleep costs, and when (and why) it stops for good. */
  sleep?: { since: number; usdPerHour: number; stopAt: number | null; why: "setting" | "active_hours" | "quiet" | null };
  /** cloud, while it boots or wakes: the current step (« Réveil… »). */
  bootPhase?: "start" | "home" | "ready" | "wake";
  /** cloud: the last boot or wake, step by step (ms since its start). */
  lastWake?: { kind: "cold" | "wake"; at: number; readyMs?: number; reachableMs?: number; homeMs?: number; screenMs?: number; thawMs?: number; browserMs?: number; desktopsMs?: number };
  os?: string;
  arch?: string;
  lastSeenAt: number | null;
  desktop: boolean; // has a screen the agent can see
  liveView: boolean; // the user can watch it (cloud: noVNC)
  /** cloud: the owner holds the controls of the screen (« Prendre les commandes »); the agent's screen actions wait. */
  userControl?: boolean;
  /** cloud: a mission works on it right now (« <Nom> est aux commandes » / « est disponible »). */
  agentActive?: boolean;
  /** cloud: auto-scaling (shared/autoscale.ts): base and maximum sizes, pinned, pending change, recent changes. */
  autoscale?: import("./autoscale").AutoscaleView;
  policy?: MachinePolicy;
  /** `ui`: the linked computer drives apps through their accessibility tree (daemon backend agent-desktop). */
  capabilities?: { computer: boolean; shell: boolean; fs: boolean; ui?: { backend: "agent-desktop"; version: string } };
  createdAt: number;
}

export type CallError = { code: import("./pcp").ErrorCode; message: string; retryable: boolean };
export type CallResult = { ok: true; result: unknown } | { ok: false; error: CallError };

// ───────────────────────────── Realtime events (agent → web) ─────────────────────────────

export type AgentEvent =
  | { type: "turn.started"; turnId: string; trigger: TurnTrigger }
  | { type: "turn.delta"; turnId: string; text: string }
  | { type: "turn.thinking"; turnId: string; text: string }
  | { type: "turn.tool"; turnId: string; name: string; status: "start" | "done" | "error"; summary: string }
  | { type: "turn.done"; turnId: string; status: "done" | "failed" | "cancelled" }
  | { type: "chat.message"; message: ChatMessage }
  | { type: "approval.upsert"; approval: ApprovalView }
  | { type: "brief.created"; brief: Brief }
  | { type: "mission.upsert"; mission: MissionView }
  | { type: "run.upsert"; run: MissionRunView }
  | { type: "run.event"; runId: string; entry: RunTimelineEntry }
  | { type: "machine.upsert"; machine: MachineView }
  | { type: "settings.updated"; settings: AgentSettings }
  /** The owner's rules changed (added by the agent, an approval « TOUJOURS », or the app): refetch them. */
  | { type: "rules.changed" }
  // The vault (« Coffre ») changed: the app reloads GET /api/vault (no value is ever broadcast).
  | { type: "vault.changed" };

// ───────────────────────────── DO RPC interfaces ─────────────────────────────

export interface InitAgentInput {
  userId: string;
  userName: string;
  timezone: string;
  email?: string | null;
  phone?: string | null;
}

export interface PostUserMessageInput {
  text: string;
  channel: Channel;
  /** Provider message id (Sendblue message_handle, Telegram "<chat>:<message_id>", email Message-ID). */
  externalId?: string;
  attachments?: { kind: "image" | "audio" | "file"; r2Key: string; mime: string; name?: string }[];
  /** Client-generated id for web messages (idempotency on retries). */
  clientId?: string;
  /**
   * Inline reply: the provider handle of the message this one answers (Sendblue reply_to.message_handle, Telegram
   * reply_to_message.message_id). iMessage and Telegram only.
   */
  replyToHandle?: string;
  /** Web chat only: the chat line this message answers (tap-to-answer buttons of a question or an approval). */
  replyToChatId?: string;
  /**
   * Telegram: a message the owner forwarded (someone else's words). Outside content: it never answers a question,
   * decides an approval or gives a consent, and the turn it starts is tainted.
   */
  forwarded?: boolean;
}

/**
 * The onboarding transition (PulseAgentRPC.completeOnboarding): what POST /api/onboarding (web form) and the
 * onboarding_complete tool (conversation by message) both apply. Omitted fields keep their current value.
 */
export interface CompleteOnboardingInput {
  userName: string;
  agentName?: string;
  agentEmoji?: string;
  persona?: Persona;
  personaNotes?: string;
  timezone?: string;
  briefTime?: string;
  primaryChannel?: Exclude<Channel, "sms">;
  /** ISO 639-1 code, only when the user's own words show their language (onboarding by message). */
  language?: string;
}

/**
 * A message from a phone account that is still getting to know its agent (created by text, not onboarded yet):
 * PulseAgentRPC.postSignupMessage. `message` is null when the message was an invite code (never shown to the model).
 */
export interface SignupMessageInput {
  init: InitAgentInput;
  /** What the agent is told once, as an <evenement type="inscription">. */
  signup: { channel: "imessage"; timezone: string; timezoneConfident: boolean };
  message: PostUserMessageInput | null;
  /** Public origin seen by the webhook (links in messages when APP_ORIGIN is unset). */
  origin?: string;
}

export interface CreateMissionInput {
  title: string;
  goal: string;
  cadenceMinutes?: number | null;
  machine?: MachineTarget;
  runNow?: boolean;
}

/** « Ton agent »: a series of generated avatar candidates (PulseAgentRPC.generateAvatars). */
export interface GenerateAvatarsInput {
  style: AvatarStyle;
  /** The user's own short description of the character (any language: translated for the model). */
  prompt: string;
  /** 2 to 4 (default 4). */
  count?: number;
}

/** Why an avatar request was refused (French message, shown as is): mapped to the HTTP error of the same code. */
export interface AvatarFailure {
  ok: false;
  error: "budget" | "unavailable" | "invalid" | "not_found";
  message: string;
}

export type GenerateAvatarsResult = { ok: true; candidates: string[]; costUsd: number } | AvatarFailure;
export type SetAvatarResult = { ok: true; settings: AgentSettings } | AvatarFailure;

export interface PulseAgentRPC {
  init(input: InitAgentInput): Promise<void>;
  getSettings(): Promise<AgentSettings>;
  updateSettings(patch: Partial<AgentSettings>): Promise<AgentSettings>;
  /**
   * « Ton agent »: generates avatar candidates with Workers AI (metered like a model call) and stores them as this
   * agent's files; a new series replaces the unchosen candidates of the previous one.
   */
  generateAvatars(input: GenerateAvatarsInput): Promise<GenerateAvatarsResult>;
  /**
   * Sets this agent's avatar: one of the last generated candidates, a file the Worker just stored from an upload
   * (under the agent's prefix), or null for the placeholder drawn from the name. Replaced files are deleted.
   */
  setAvatar(input: { fileKey: string; source: AgentAvatar["source"] } | null): Promise<SetAvatarResult>;
  postUserMessage(input: PostUserMessageInput): Promise<{ accepted: boolean; turnId: string | null; handledAs: "turn" | "approval" | "question" | "command" | "duplicate" }>;
  /**
   * The onboarding transition shared by the web form (POST /api/onboarding: with the greeting turn) and the
   * onboarding_complete tool (without it: the agent's own reply is the welcome). Idempotent.
   */
  completeOnboarding(input: CompleteOnboardingInput): Promise<AgentSettings>;
  /**
   * Sign-up by text (Sendblue webhook): idempotent init, the sign-up facts told once, then the message exactly like
   * postUserMessage — or, for an invite code, a neutral sign-up event turn instead (once per account).
   */
  postSignupMessage(input: SignupMessageInput): Promise<{ accepted: boolean; turnId: string | null; handledAs: "turn" | "approval" | "question" | "command" | "duplicate" }>;
  getChatLog(opts?: { before?: number; limit?: number }): Promise<ChatMessage[]>;
  /**
   * The owner's tapback from the app on one of the agent's lines (V2 §6.5), or its removal: recorded and shown like an
   * iMessage tapback (a line carrying `reaction`, grouped on its target), never answered. null: not one of its lines.
   */
  reactToLine(input: { targetId: string; emoji: string; removed?: boolean }): Promise<ChatMessage | null>;
  getDashboard(): Promise<Dashboard>;
  /** Pulse's generative summary of the day (json-render spec + three suggestion chips), cached per inputs. */
  getPulseSummary(): Promise<import("./pulse-summary").PulseSummary>;
  listBriefs(limit?: number): Promise<Brief[]>;
  listApprovals(status?: ApprovalStatus | "all"): Promise<ApprovalView[]>;
  decideApproval(input: DecideApprovalInput): Promise<ApprovalView>;
  /**
   * Approval decided by an inbound code reply (OUI K7, NON K7, TOUJOURS K7: approve and create the allow rule). Returns
   * null if no pending approval has that code.
   */
  decideApprovalByCode(code: string, decision: "approve" | "reject" | "always", via: "sms" | "imessage" | "telegram"): Promise<ApprovalView | null>;
  /** The owner's rules (allow | ask | deny), most recent first. */
  listRules(): Promise<import("./rules").RuleView[]>;
  /** Adds a rule from the web app (created_from "app"); an identical rule is returned as is. */
  addRule(input: import("./rules").RuleInput): Promise<import("./rules").RuleView>;
  /** Removes a rule; false when it does not exist. */
  removeRule(id: string): Promise<boolean>;
  ingestSignal(signal: SignalInput): Promise<{ stored: boolean }>;
  /** Veilles (watches): one action of the web app, then the whole list (`error`: a French message, nothing changed). */
  watches(op: WatchOp): Promise<{ view: WatchesView; error: string | null }>;
  listMissions(): Promise<MissionView[]>;
  createMission(input: CreateMissionInput): Promise<MissionView>;
  updateMission(id: string, patch: Partial<Pick<MissionView, "title" | "goal" | "cadenceMinutes" | "machine" | "status" | "notes">>): Promise<MissionView>;
  runMissionNow(id: string): Promise<MissionRunView>;
  listRuns(limit?: number): Promise<MissionRunView[]>;
  getRun(runId: string): Promise<RunDetail | null>;
  cancelRun(runId: string): Promise<MissionRunView | null>;
  /** Same as answerQuestion({runId, answer, via: "web"}) for the pending question of that run. */
  answerRun(runId: string, answer: string): Promise<MissionRunView | null>;
  listTurns(limit?: number): Promise<TurnView[]>;
  /** Recent fan-outs of parallel sub-agents (activity page). */
  listFanouts(limit?: number): Promise<import("./subagents").FanoutView[]>;
  /** Called by MissionWorkflow steps. Idempotent per (runId, iter). */
  missionIterate(runId: string, iter: number): Promise<MissionIterResult>;
  missionResume(runId: string, waitFor: string, payload: unknown): Promise<void>;
  /** Called by MissionWorkflow when the run cannot continue (step retries exhausted, unexpected error). Idempotent. */
  missionFail(runId: string, error: string): Promise<void>;
  missionFinalize(runId: string): Promise<void>;
  /** Re-arms every proactive schedule; safe to call often (hourly cron safety net). */
  ensureSchedules(): Promise<void>;
  addPushSubscription(sub: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string }): Promise<void>;
  removePushSubscription(endpoint: string): Promise<void>;
  /** Machine availability changed (called by MachineDO) — refreshes caches and may wake a waiting run. */
  machineChanged(machine: MachineView): Promise<void>;
  /** MemoryStore → agent: due dates changed (arm/cancel follow-ups immediately). */
  onDueChanged(changes: DueChanges): Promise<void>;
  /**
   * MemoryStore → agent: memories were erased or corrected (explicit forget/correction). The agent drops pending
   * follow-ups/wakeups tied to forgotten facts and never mentions forgotten content again. Idempotent.
   */
  onMemoryChanged(change: MemoryChangeNotice): Promise<void>;
  /** Channels → agent: a channel link (phone, Telegram) changed: forget the cached identity so deliveries use it at once. */
  identityChanged(): Promise<void>;
  /** Webhook → agent: an app connection changed state (e.g. Composio OAuth expired). */
  onAppConnectionChanged(toolkit: string, status: "active" | "expired" | "failed" | "removed"): Promise<void>;
  /** iCloud (Calendrier et Mail): the card's view (never the password). */
  icloudStatus(): Promise<IcloudView>;
  /** Tests the app-specific password with Apple (calendar, then mail), then stores it encrypted. Nothing stored on failure. */
  icloudConnect(input: IcloudConnectInput): Promise<IcloudConnectResult>;
  /** Switches a service on (live test with the stored password) or off, or changes the iCloud Mail address. */
  icloudUpdate(patch: IcloudUpdateInput): Promise<IcloudConnectResult>;
  /** Forgets the password and the iCloud state; returns the disconnected view. */
  icloudDisconnect(): Promise<IcloudView>;
  /** Mission run question answered (web, SMS code "Q4 …", or main-conversation tool). */
  answerQuestion(input: { runId: string; questionId?: string; answer: string; via: "web" | "sms" | "imessage" | "telegram" | "agent" }): Promise<MissionRunView | null>;
  /** Owner/admin: stop every schedule (account disabled). */
  disable(): Promise<void>;
  /** E2E only (env.E2E === "1"): fire a schedule callback now (payload defaults to a fresh occurrence). */
  e2eFire(callback: E2eCallback, payload?: unknown): Promise<unknown>;
  /** E2E only: inspect internal state (schedules, turns, outbox, signals, proactive log, iCloud state — ciphertext only, prewarms). */
  e2eInspect(what: "schedules" | "turns" | "outbox" | "signals" | "proactive" | "icloud" | "prewarm" | "approvals" | "vault" | "vaultExport" | "wakes" | "images" | "outboundFiles"): Promise<unknown>;
  /** E2E only: shift this agent's clock (every local-time decision goes through one now() helper). */
  e2eSetClock(offsetMs: number): Promise<void>;
  /** Writes a JSONL export to R2 and returns its key. */
  exportData(): Promise<{ r2Key: string; count: number }>;
  /** Closes the user's open WebSockets (all, or only those opened with this session) — logout, session deletion. */
  closeConnections(sessionHash?: string): Promise<void>;
  deleteAll(): Promise<void>;
}

export type E2eCallback =
  | "morningBrief"
  | "eveningRecap"
  | "checkin"
  | "sweep"
  | "nightly"
  | "flushOutbox"
  | "followUps"
  | "wakeup"
  | "missionDue"
  | "ensureSchedules"
  | "reengage"
  | "meetingPrep"
  | "digestFlush"
  /** A watch run (« veille »): payload.watchId, else the next due watch. */
  | "watch"
  /** E2E only: arms a simulated isolate death right after the next run of `payload.tool` (tool resume specs). */
  | "dieAfterTool"
  /** The main conversation's cache keep-warm ping (engine/keep-warm.ts), when due. */
  | "keepWarm"
  /** E2E only: stands for an account onboarded before real contact cards (no card sent, schema v14 flag set again). */
  | "contactCardLegacy"
  /** The anticipated wake of the cloud computer before a scheduled mission or a computer watch (agent/wake.ts). */
  | "computerWake"
  /**
   * E2E only: `payload.maxIters` (step budget of the runs started from now on) and `payload.controlWaitMs` (how long a
   * paused iteration waits inside its step while the owner holds the controls).
   */
  | "missionLimits"
  /**
   * E2E only: `payload.ownAtMainPrice` prices a spontaneous message's own prompt at the main model (the pricing before
   * the background model), so a short conversation's check-in still runs on its cached prefix: the sharing mechanism.
   */
  | "sharePricing"
  /**
   * E2E only: makes `payload.runId` a run of the former in-computer executor (« hermes »: its setup says so, its mission
   * conversation is gone), as runs in progress were when it was removed: the next iteration takes it over.
   */
  | "legacyRun";

/** Memories erased or corrected by an explicit write (PulseAgentRPC.onMemoryChanged). Contents are never logged. */
export interface MemoryChangeNotice {
  forgotten: { factId: string; content: string }[];
  corrected: { factId: string; before: string; after: string }[];
}

export interface DueChanges {
  upserted: { factId: string; dueAt: number; dateOnly: boolean; content: string; userOriginated: boolean; sensitivity?: MemorySensitivity }[];
  removed: string[];
}

export interface MemoryStoreRPC {
  init(userId: string): Promise<void>;
  appendEpisodes(episodes: EpisodeInput[]): Promise<{ appended: number }>;
  recall(q: RecallQuery): Promise<RecallResult>;
  /** Per-turn automatic recall (no LLM): "" when nothing relevant. */
  autoRecall(text: string, opts?: { budgetTokens?: number; audience?: MemoryAudience }): Promise<string>;
  save(input: SaveMemoryInput): Promise<MemoryItemView>;
  /**
   * Same as save, but a content the store refuses by rule (secret, routine behaviour, untrusted instruction, unknown
   * correction target) comes back as `{ refused }` (French, for the model) instead of an exception across the RPC.
   */
  trySave(input: SaveMemoryInput): Promise<{ item: MemoryItemView } | { refused: string }>;
  update(id: string, patch: Partial<Pick<MemoryItemView, "content" | "importance" | "pinned" | "dueAt" | "kind" | "sensitivity">>): Promise<MemoryItemView | null>;
  forget(id: string, hard: boolean, opts?: { reason?: MemoryForgetReason; via?: "web" | "agent" }): Promise<boolean>;
  /** "Pourquoi tu sais ça ?" */
  explain(id: string): Promise<MemoryExplanation | null>;
  /** Last night's sleep line and the memories it set aside (memory page). */
  sleepReport(limit?: number): Promise<MemorySleepReport>;
  /** Puts back a memory the sleep set aside (never reviewed again); null when it is not one. */
  restore(id: string): Promise<MemoryItemView | null>;
  /** E2E only (throws unless config(env).e2e). */
  e2e(input: MemoryE2eOp): Promise<unknown>;
  list(q: MemoryListQuery): Promise<{ items: MemoryItemView[]; cursor: string | null }>;
  profile(): Promise<ProfileBlockView[]>;
  /** Profile blocks as one French text block for the agent context (+ a version for change detection). */
  profileContext(): Promise<{ text: string; version: number }>;
  extractPending(opts?: { force?: boolean }): Promise<{ extracted: number }>;
  /** Starts (or continues) the nightly consolidation, which then runs stepwise from the MemoryStore's own alarm. Returns at once. */
  consolidate(): Promise<{ started: boolean; phase: string }>;
  due(fromMs: number, toMs: number): Promise<MemoryItemView[]>;
  importRecords(records: { content: string; kind?: MemoryKind; createdAt?: number; source?: string }[]): Promise<{ imported: number }>;
  /** Writes a JSONL export (no embeddings) to R2 FILES and returns its key (RPC messages are limited to 32 MiB). */
  exportAll(): Promise<{ r2Key: string; count: number }>;
  /** Account deletion: erases everything and tombstones the store (every later write is refused). */
  wipe(): Promise<void>;
}

export interface MachineInitInput {
  machineId: string;
  userId: string;
  kind: "cloud" | "local";
  name: string;
  size?: MachineSize;
  policy?: MachinePolicy;
  os?: string;
  arch?: string;
}

/** Who is calling a machine: MachineDO refuses callers other than the machine's owner. */
export interface MachineCaller {
  userId: string;
  runId?: string;
}

export interface MachineRPC {
  init(input: MachineInitInput): Promise<MachineView>;
  status(): Promise<MachineView>;
  /** cloud: boots (restoring the home directory); local: no-op. */
  start(): Promise<MachineView>;
  /** cloud: saves the home directory then stops; local: no-op. */
  stop(): Promise<MachineView>;
  setSize(size: MachineSize): Promise<MachineView>;
  setIdleStop(minutes: number): Promise<MachineView>;
  setPolicy(policy: MachinePolicy): Promise<MachineView>;
  /** Executes one pcp/1 method on the machine (starting a cloud machine if needed). */
  call(caller: MachineCaller, method: import("./pcp").Method, params: unknown, timeoutMs?: number): Promise<CallResult>;
  /** Long-lived missions keep a cloud machine awake. */
  hold(runId: string, ttlMs: number): Promise<void>;
  release(runId: string): Promise<void>;
  revoke(): Promise<void>;
}

// ───────────────────────────── REST API envelopes ─────────────────────────────

export interface ApiError {
  error: string; // machine-readable code: "unauthorized" | "forbidden" | "not_found" | "invalid" | "rate_limited" | "budget" | "unavailable" | "conflict"
  message: string; // French, user-presentable
}

export interface MeView {
  id: string;
  email: string | null;
  phone: string | null;
  name: string;
  timezone: string;
  role: "owner" | "member";
  onboarded: boolean;
  createdAt: number;
}

export interface ConnectionsView {
  imessage: { available: boolean; linked: string | null; number: string | null };
  telegram: { available: boolean; linked: boolean; botUsername: string | null };
  email: { available: boolean; address: string | null };
  push: { available: boolean; publicKey: string | null; subscriptions: number };
  apps: { available: boolean; connected: { toolkit: string; status: string }[] };
  icloud: IcloudView;
}

// ───────────────────────────── iCloud (Calendrier et Mail) ─────────────────────────────

/** Why an iCloud operation failed (tool results, API errors, the card's last problem). */
export type IcloudErrorCode =
  | "not_app_password"
  | "invalid_input"
  | "auth_failed"
  | "mail_address_refused"
  | "auth_invalid"
  | "not_connected"
  | "service_off"
  | "network"
  | "throttled"
  | "protocol"
  | "unavailable"
  | "not_found"
  | "conflict"
  | "recurring_unsupported"
  | "too_large"
  | "rejected"
  | "recipients_refused"
  | "sender_refused"
  | "daily_limit"
  | "unknown_outcome"
  | "busy"
  | "not_watched";

export interface IcloudServiceView {
  enabled: boolean;
  /** "invalid": Apple refused the password after the connection (revoked, or the Apple password changed). */
  state: "active" | "invalid" | "off";
  watched: boolean;
  lastSyncAt: number | null;
  lastError: { code: IcloudErrorCode; message: string; at: number } | null;
  backoffUntil: number | null;
}

export interface IcloudView {
  /** False when this server cannot store the password encrypted (no keyring): the feature is off. */
  available: boolean;
  connected: boolean;
  appleId: string | null;
  mailAddress: string | null;
  connectedAt: number | null;
  mail: IcloudServiceView;
  calendar: IcloudServiceView & { calendars: { id: string; name: string; color: string | null }[] };
}

export interface IcloudConnectInput {
  /** The Apple ID's e-mail address (the CalDAV login). */
  appleId: string;
  /** The iCloud Mail address (SMTP login and From); defaults to the Apple ID when it is an @icloud.com/@me.com/@mac.com one. */
  mailAddress?: string | null;
  /** The app-specific password (xxxx-xxxx-xxxx-xxxx), never stored in clear nor returned. */
  password: string;
  mail: boolean;
  calendar: boolean;
}

export interface IcloudUpdateInput {
  mail?: boolean;
  calendar?: boolean;
  mailAddress?: string | null;
}

export type IcloudConnectResult = { ok: true; icloud: IcloudView } | { ok: false; code: IcloudErrorCode; message: string };
